import * as React from "react";
import { WithStyles } from "@material-ui/core";
import UniversalTable from "./UniversalTable/UniversalTable";
import { IUniversalTableColumn, IUniversalTableAddAction } from "../components/UniversalTable/interfaces";
import { styles } from "../components/UniversalTable/UniversalTable.styles";
import { withStyles } from "@material-ui/core/styles";
import { convertDateStringToNumber, today } from "../../utilities/dateTimeUtils";
import { DATE_FORMAT } from "../../constants/TIME";
import { useTranslation } from "react-i18next";
import { BaseCurrenciesComponent } from "../views/admin/BaseCurrenciesComponent";

export interface IDateRangesTable extends WithStyles<typeof styles> {
    dateRanges: any;
    handleEditDateRange: any;
    handleAddDateRange: any;
    handleDeleteFinished: any;
    isRequestingDateRanges: boolean;
    oneDateRange?: boolean;
    isFilterableSubscription?: boolean;
}

const DateRangesTablePure: React.FunctionComponent<IDateRangesTable> = ({
    dateRanges,
    handleEditDateRange,
    handleAddDateRange,
    handleDeleteFinished,
    isRequestingDateRanges,
    oneDateRange,
    isFilterableSubscription,
    classes
}: IDateRangesTable) => {
    const { t } = useTranslation();

    const tableRef = React.createRef();

    const [displayedSubscriptionIndex, setDisplayedSubscriptionIndex] = React.useState<number>(null);
    const [detailsDialogOpen, setDetailsDialogOpen] = React.useState<boolean>(false);

    const handleShowDetails = index => {
        setDisplayedSubscriptionIndex(index);
        setDetailsDialogOpen(true);
    };

    const defaultStartDate = convertDateStringToNumber(today().format(DATE_FORMAT));
    const defaultEndDate = 0;
    const defaultVisibilityEndDate = 0;

    const dateRangesTableColumns: IUniversalTableColumn[] = [
        {
            type: "date_with_option",
            title: t("admin.manageSubscriptionUserDateRangesView.startDate"),
            field: "startDate",
            label: t("admin.manageSubscriptionUserView.unrestricted"),
            defaultValue: defaultStartDate,
            validate: rowData => {
                return rowData?.startDate === 0 || rowData?.endDate === 0
                    ? true
                    : rowData?.startDate > rowData?.endDate
                    ? { isValid: false, helperText: t("admin.manageSubscriptionUserDateRangesView.startDateError") }
                    : true;
            }
        },
        {
            type: "date_with_option",
            title: t("admin.manageSubscriptionUserDateRangesView.endDate"),
            field: "endDate",
            label: t("admin.manageSubscriptionUserView.unrestricted"),
            defaultValue: defaultEndDate,
            validate: rowData => {
                return rowData?.startDate === 0 || rowData?.endDate === 0
                    ? true
                    : rowData?.startDate > rowData?.endDate
                    ? { isValid: false, helperText: t("admin.manageSubscriptionUserDateRangesView.endDateError") }
                    : true;
            }
        },
        ...(isFilterableSubscription ? [{
            type: "date_with_option",
            title: "Actual start date",
            field: "actualStartDate",
            label: t("admin.manageSubscriptionUserView.unrestricted")
        } as IUniversalTableColumn] 
        : []),
        ...(isFilterableSubscription ? [{
            type: "link",
            title: "Details",
            linkTitle: "Show details",
            onClick: (index) => handleShowDetails(index)
        } as IUniversalTableColumn]
        : []),
        {
            type: "boolean",
            title: "Trial",
            field: "trial"
        },
        {
            type: "boolean",
            title: "Sales trial",
            field: "salesTrial",
            editable: "never"
        },
        {
            type: "visibility_select",
            title: "Visibility start date",
            field: "visibilityStartDate",
            dateField: "startDate",
            defaultValue: defaultStartDate,
            categories: ["start_date", "unrestricted", "custom"],
            validate: rowData => {
                return rowData?.visibilityStartDate === 0 || rowData?.visibilityEndDate === 0
                    ? true
                    : rowData?.visibilityStartDate > rowData?.visibilityEndDate
                    ? { isValid: false, helperText: t("admin.manageSubscriptionUserDateRangesView.startDateError") }
                    : true;
            }
        },
        {
            type: "visibility_select",
            title: "Visibility end date",
            field: "visibilityEndDate",
            dateField: "endDate",
            defaultValue: defaultVisibilityEndDate,
            categories: ["plus_2_years", "plus_7_days", "unrestricted", "custom"],
            validate: rowData => {
                return rowData?.visibilityStartDate === 0 || rowData?.visibilityEndDate === 0
                    ? true
                    : rowData?.visibilityStartDate > rowData?.visibilityEndDate
                    ? { isValid: false, helperText: t("admin.manageSubscriptionUserDateRangesView.endDateError") }
                    : true;
            }
        },
        {
            type: "actions",
            title: t("admin.taskView.actions"),
            actions: [
                {
                    name: "edit",
                    tooltip: t("admin.manageSubscriptionUserDateRangesView.editDateRange"),
                    onEditFinished: (index, newValue) => handleEditDateRange(index, newValue)
                },
                {
                    name: "add",
                    disabled: isFilterableSubscription || (oneDateRange === true && dateRanges.length !== 0),
                    tooltip: t("admin.manageSubscriptionUserDateRangesView.addDateRange"),
                    onAddFinished: newRange => handleAddDateRange(newRange),
                    disabledTooltip: isFilterableSubscription
                        ? "Date ranges for filterable subscriptions can be added only via Subscription request."
                        : "Date range cannot be added."
                } as IUniversalTableAddAction,
                {
                    name: "delete",
                    tooltip: t("admin.manageSubscriptionUserDateRangesView.deleteDateRange"),
                    onDeleteFinished: index => handleDeleteFinished(index),
                    deleteQuestion: t("admin.manageSubscriptionUserDateRangesView.deleteQuestion")
                }
            ]
        }
    ];

    return (
        <>
            <UniversalTable
                tableRef={tableRef}
                columns={dateRangesTableColumns}
                data={dateRanges}
                isLoading={isRequestingDateRanges}
                emptyDataMessage={t("admin.manageSubscriptionUserDateRangesView.noDataRanges")}
                pagingDisabled={oneDateRange}
            />

            {dateRanges && isFilterableSubscription && displayedSubscriptionIndex !== null
                ? <BaseCurrenciesComponent
                currencies={dateRanges[displayedSubscriptionIndex].levelTwoItems.map(currency => currency.name).join(", ")}
                detailsDialogOpen={detailsDialogOpen}
                setDetailsDialogOpen={setDetailsDialogOpen}
                subscriptionRequestId={dateRanges[displayedSubscriptionIndex].subscriptionRequestId}
                />
                : null
            }
        </>
    );
};

export const DateRangesTable = withStyles(styles)(DateRangesTablePure);